import React, { useEffect, useState } from 'react';
import { IScheduler, ISendGridMessage, useGetSendGridMessagesQuery } from '../../generated/graphql';
import { Box, Button, Divider, InputBase } from '@mui/material';
import { Colors } from '../../utils/theme';
import MessageDetails from './MessageDetails';
import Loader from '../Common/Loader';
import DialogBox from '../Common/Layout/DialogBox';
import DenseTable from '../Common/Tables/DenseTable';
import InputComponent from '../Common/Fields/InputComponent';
import { FiberManualRecord } from '@mui/icons-material';
import SearchInput from '../RecipientListWizard/SearchInput';

interface IProps {
  handleClose: () => void;
  scheduler: IScheduler;
}

const button = {
  borderRadius: '15px',
  width: '95px',
  height: '30px',
  fontSize: '12px',
  fontFamily: 'Overpass'
};

const buttonColors = {
  lightGreen: '#D3F7A8',
  darkGreen: '#67A026',
  lightRed: '#FFDCDC',
  darkRed: '#D13E3E'
};

const SchedulerDetails: React.FunctionComponent<IProps> = ({ scheduler, handleClose }: IProps) => {
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [sentPressed, setSentPressed] = useState(false);
  const [failedPressed, setFailedPressed] = useState(false);
  const [allActivityLogs, setAllActivityLogs] = useState<ISendGridMessage[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ISendGridMessage[]>([]);
  const { data, loading } = useGetSendGridMessagesQuery({
    variables: { scheduler_id: Number(scheduler.id) },
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (data?.result && data.result.length) {
      const successes = data.result.filter((d) => d?.status === 'delivered');
      setAllActivityLogs(data.result as ISendGridMessage[]);
      setFilteredLogs(data.result as ISendGridMessage[]);
      setSuccessCount(successes.length);
      setFailureCount(data.result.length - successes.length);
    }
  }, [data]);

  const detailPanel = (rowData: ISendGridMessage) => {
    return <MessageDetails messageId={rowData.sg_message_id} />;
  };

  const handleSentButtonClicked = () => {
    if (sentPressed) {
      setFilteredLogs(allActivityLogs);
    } else {
      setFilteredLogs(allActivityLogs.filter((al) => al.status === 'delivered'));
      setFailedPressed(false);
    }
    setSentPressed(!sentPressed);
  };

  const handleFailedClicked = () => {
    if (failedPressed) {
      setFilteredLogs(allActivityLogs);
    } else {
      setFilteredLogs(allActivityLogs.filter((al) => al.status === 'not delivered'));
      setSentPressed(false);
    }
    setFailedPressed(!failedPressed);
  };

  const renderStatus = (status: string) => {
    let color = '';
    if (status === 'processed') {
      color = Colors.warning;
    } else if (status === 'delivered') {
      color = Colors.green;
    } else {
      color = Colors.red;
    }

    return <InputBase value={status} style={{ color, fontWeight: 'bold' }} />;
  };

  const columns = [
    {
      title: 'Email',
      field: 'email',
      width: 500
    },
    {
      title: 'Status',
      field: 'status',
      width: 300,
      render: (rowData: ISendGridMessage) => renderStatus(rowData.status)
    },
    {
      title: 'Click Count',
      field: 'clicks_count',
      width: 200
    },
    {
      title: 'Opens Count',
      field: 'opens_count',
      width: 200
    }
  ];

  const filterValues = (text: string) => {
    if (allActivityLogs) {
      const filtered = allActivityLogs.filter((el) =>
        el.email.toLocaleLowerCase().includes(text.toLocaleLowerCase())
      );
      setFilteredLogs(filtered);
    }
  };

  return (
    <Box>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <DialogBox
          id="scheduler-details-log"
          title="Job Details"
          width={1240}
          height={900}
          onClose={handleClose}
        >
          <Box
            sx={{
              height: '180px',
              background: Colors.lightBlue,
              px: '2.5%',
              py: '25px',
              display: 'flex',
              flexDirection: 'column',
              gap: '25px'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
              <InputComponent
                id="job-title"
                label="Job Title"
                value={scheduler.name}
                disabled
                withoutBackground
                boldText
              />
              <Divider orientation="vertical" flexItem />
              <InputComponent
                id="template-title"
                label="Template Title"
                value={scheduler.template.title}
                disabled
                withoutBackground
                boldText
              />
              <Divider orientation="vertical" flexItem />
              <InputComponent
                id="total-recipients"
                label="Total Recipients"
                value={scheduler.mails_sent as number}
                disabled
                withoutBackground
              />
              <Divider orientation="vertical" flexItem />
              <InputComponent
                id="success-count"
                label="Success Count"
                value={successCount}
                disabled
                withoutBackground
                startAdornment={
                  <FiberManualRecord fontSize={'small'} style={{ color: Colors.green }} />
                }
              />
              <InputComponent
                id="failure-count"
                label="Failure Count"
                value={failureCount}
                disabled
                withoutBackground
                startAdornment={
                  <FiberManualRecord fontSize={'small'} style={{ color: Colors.red }} />
                }
              />
            </Box>
            <Divider />
            <Box sx={{ display: 'flex' }}>
              <SearchInput filter={filterValues} id={'scheduler-details-search-input'} />
              <Box sx={{ display: 'flex', width: '500px', alignItems: 'center', gap: '10px' }}>
                <InputBase
                  id="job-details-filter-by"
                  data-testid="job-details-filter-by"
                  value={'Filter by:'}
                  disabled
                  style={{
                    fontSize: '12px',
                    fontFamily: 'Overpass'
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'right',
                      marginTop: '7px'
                    }
                  }}
                />
                <Button
                  id="sent-button"
                  data-testid="sent-button"
                  onClick={handleSentButtonClicked}
                  sx={{
                    ...button,
                    background: sentPressed ? buttonColors.darkGreen : buttonColors.lightGreen,
                    color: sentPressed ? buttonColors.lightGreen : buttonColors.darkGreen,
                    '&:hover': {
                      ...button,
                      background: sentPressed ? buttonColors.lightGreen : buttonColors.darkGreen,
                      color: sentPressed ? buttonColors.darkGreen : buttonColors.lightGreen
                    }
                  }}
                >
                  SENT ({successCount})
                </Button>
                <Button
                  id="failed-button"
                  data-testid="failed-button"
                  onClick={handleFailedClicked}
                  sx={{
                    ...button,
                    background: failedPressed ? buttonColors.darkRed : buttonColors.lightRed,
                    color: failedPressed ? buttonColors.lightRed : buttonColors.darkRed,
                    '&:hover': {
                      ...button,
                      background: failedPressed ? buttonColors.lightRed : buttonColors.darkRed,
                      color: failedPressed ? buttonColors.darkRed : buttonColors.lightRed
                    }
                  }}
                >
                  FAILED ({failureCount})
                </Button>
              </Box>
            </Box>
          </Box>
          <Box sx={{ height: '655px' }}>
            <DenseTable
              columns={columns}
              data={filteredLogs}
              loading={false}
              handleDetails={() => console.log('details')}
              handleRemove={() => console.log('remove')}
              detailPanel={detailPanel}
            />
          </Box>
        </DialogBox>
      )}
    </Box>
  );
};

export default SchedulerDetails;
